// SDM — IN INFINITUM · la galerie des cartes (snippets/sdm-carte-galerie.liquid ; 26/10, demande JB, à la manière
// d'Arte) : les vues suivantes d'un produit dans sa case, sur l'accueil comme dans les collections.
//
// À la souris, les vues sont empilées (assets/sdm-collection.css) : entrer sur la carte montre la deuxième — le dos,
// d'ordinaire —, sortir rend la première ; rien d'autre (27/10, demande JB). Au doigt, la case est une bande qui défile
// vue par vue (CSS seul) ; le script n'y tient que la vue active. Sans points (demande JB, 26/10).
// Chargement : les vues suivantes arrivent sans adresse (`data-src`, `data-srcset`), rien ne se charge avec la page.
// Une carte regardée (60 % à l'écran pendant 1,2 s) charge sa deuxième vue, en priorité basse, pour qu'elle soit
// prête au survol ou au premier glissé ; le survol la charge s'il la devance, le premier glissé charge les autres (à la
// souris, elles ne servent pas). Rien d'avance en mode économie de données ni en 2G. Le vol vers la fiche
// (assets/sdm-fiche.js) part de la vue affichée.

const SOURIS = matchMedia('(hover: hover) and (pointer: fine)');
const ECO = !!navigator.connection?.saveData || /2g$/.test(navigator.connection?.effectiveType ?? '');
const REGARD_MS = 1200;

const vues = (g) => g.querySelectorAll(':scope > .sdm-galerie__vue');
const voulue = new WeakMap();   // galerie → la vue demandée (elle s'affiche une fois chargée)
const affichee = new WeakMap(); // galerie → la vue affichée

// la vue `k` devient la vue active, et le coin / les lots de la case s'effacent hors de la première
function marquer(g, k) {
  if ((affichee.get(g) ?? 0) === k) return;
  affichee.set(g, k);
  vues(g).forEach((v, i) => v.classList.toggle('is-active', i === k));
  g.parentElement?.classList.toggle('sdm-tile--suite', k > 0);
}

// les vues suivantes, jusqu'à la `jusqua`-ième : elles reçoivent leur adresse (le jeu de largeurs d'abord, pour un
// seul téléchargement, celui que choisit `sizes`)
function charger(g, jusqua = Infinity) {
  const liste = vues(g);
  for (let i = 1; i < liste.length && i <= jusqua; i++) {
    const img = liste[i].querySelector('img[data-src]');
    if (!img) continue;
    img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');
  }
}

// à la souris : la vue `k` s'affiche dès qu'elle est chargée — jusque-là, la précédente reste
function montrer(g, k) {
  voulue.set(g, k);
  const img = vues(g)[k]?.querySelector('img');
  if (!img) return;
  if (img.complete && img.naturalWidth) marquer(g, k);
  else img.addEventListener('load', () => { if (voulue.get(g) === k) marquer(g, k); }, { once: true });
}

/* ---------- à la souris ---------- */

// La deuxième vue au survol, la première à la sortie. Jusqu'au 27/10, les vues suivantes se succédaient au fil du
// pointeur dans la case (« elles défilent seules, c'est un peu archaïque », JB) ; des flèches les ont remplacées le
// même jour, retirées aussitôt (« je trouve les flèches cheap ») : « juste la seconde image en hover ».
let survolee = null;

// La fiche ou la fenêtre de l'îlot Chrome ouverte par-dessus la carte : la vue d'où le vol est parti reste affichée,
// le vol du retour revient s'y poser ; le pointeur qui passe dessus ne compte pas (la sortie suivante rend la première).
const recouverte = (cible) =>
  !!cible?.closest?.('.sdm-fiche-layer, .sdm-pop') || document.documentElement.matches('.sdm-fiche-open, .sdm-pop-open');

function quitter() {
  if (survolee) montrer(survolee, 0);
  survolee = null;
}

document.addEventListener('pointerover', (e) => {
  if (e.pointerType !== 'mouse' || !SOURIS.matches || recouverte(e.target)) return;
  const g = e.target.closest?.('.sdm-piece')?.querySelector('[data-sdm-galerie]');
  if (g === survolee) return;
  quitter();
  if (!g) return;
  survolee = g;
  charger(g, 1);
  montrer(g, 1);
}, { passive: true });

document.addEventListener('pointerout', (e) => {
  if (!survolee || survolee.closest('.sdm-piece')?.contains(e.relatedTarget) || recouverte(e.relatedTarget)) return;
  quitter();
}, { passive: true });

/* ---------- au doigt : la bande ---------- */

// Retour à la première vue (26/10, demande JB : « après un petit moment, réinitialiser la position des images ») : une
// bande glissée y revient 4 s après le dernier geste, en glissant si elle est à l'écran, et d'emblée, sans mouvement,
// dès qu'elle en sort. Pas tant que la fiche est ouverte : le vol du retour revient se poser sur la vue d'où il est
// parti. À la souris, la sortie de la carte rend déjà la première vue.
const RETOUR_MS = 4000;
const REDUIT = matchMedia('(prefers-reduced-motion: reduce)');
const retours = new WeakMap(); // galerie → minuteur du retour
const sortie = 'IntersectionObserver' in window
  ? new IntersectionObserver((entrees) => {
    for (const { target: g, isIntersecting } of entrees) if (!isIntersecting) revenir(g, false);
  })
  : null;

// la vue de la bande à l'écran (une vue : la case plus un pixel, assets/sdm-collection.css)
const indice = (g) => Math.round(g.scrollLeft / (g.scrollWidth / vues(g).length || 1));

function attendre(g) {
  clearTimeout(retours.get(g));
  retours.set(g, setTimeout(() => revenir(g, true), RETOUR_MS));
  sortie?.observe(g);
}

function annuler(g) {
  clearTimeout(retours.get(g));
  retours.delete(g);
  sortie?.unobserve(g);
}

function revenir(g, doux) {
  if (indice(g) === 0) return annuler(g);
  if (document.querySelector('.sdm-fiche-layer.on') || g.querySelector('.sdm-flying')) return attendre(g);
  annuler(g);
  g.scrollTo({ left: 0, behavior: doux && !REDUIT.matches ? 'smooth' : 'auto' });
}

const enAttente = new WeakSet();
document.addEventListener('scroll', (e) => {
  const g = e.target;
  if (!(g instanceof Element) || !g.matches('[data-sdm-galerie]') || enAttente.has(g)) return;
  enAttente.add(g);
  charger(g);
  requestAnimationFrame(() => {
    enAttente.delete(g);
    const k = indice(g);
    marquer(g, k);
    if (k > 0) attendre(g);
    else annuler(g);
  });
}, { capture: true, passive: true });

/* ---------- les photos à fond perdu : toute la case ---------- */

// Les photos du produit sont carrées, les cases en 4:5 (26/10, demande JB : « que les photos occupent 100 % de la
// carte »). Une photo qui touche le bas et les deux côtés de son fichier — un détail à fond perdu, un cadrage sur le
// col détouré au-dessus — remplit la case (`is-plein` : object-fit cover, rognée sur les côtés). Un détouré posé au
// milieu de son fichier, avec sa marge, garde la sienne : agrandi, il perdrait ses manches. Lu une fois sur l'image
// chargée, réduite à 24 × 24 px : l'opacité de sa dernière ligne et de ses deux colonnes de bord. Les fichiers ne
// changent pas (ils servent aussi la fiche).
const C = 24;
let toile = null;
function cadrer(img) {
  if (img.dataset.sdmCadre !== undefined || !img.complete || !img.naturalWidth) return;
  img.dataset.sdmCadre = '';
  try {
    toile ??= Object.assign(document.createElement('canvas'), { width: C, height: C });
    const ctx = toile.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, C, C);
    ctx.drawImage(img, 0, 0, C, C);
    const px = ctx.getImageData(0, 0, C, C).data;
    const opaque = (x, y) => px[(y * C + x) * 4 + 3] > 230;
    let bas = 0, gauche = 0, droite = 0;
    for (let i = 0; i < C; i++) {
      bas += opaque(i, C - 1);
      gauche += opaque(0, i);
      droite += opaque(C - 1, i);
    }
    if (bas >= C * 0.85 && gauche >= C * 0.5 && droite >= C * 0.5) img.classList.add('is-plein');
  } catch { /* image d'un autre domaine (l'aperçu de l'éditeur) : elle garde son cadrage */ }
}

// avant les écouteurs de l'image (montrer) : la vue paraît déjà cadrée
document.addEventListener('load', (e) => {
  if (e.target instanceof HTMLImageElement && e.target.matches('.sdm-galerie__vue img')) cadrer(e.target);
}, true);
const cadrerTout = (racine) => racine.querySelectorAll('.sdm-galerie__vue img').forEach(cadrer);
cadrerTout(document);
document.addEventListener('shopify:section:load', (e) => cadrerTout(e.target));

/* ---------- la deuxième vue, d'avance, pour une carte regardée ---------- */

if (!ECO && 'IntersectionObserver' in window) {
  const minuteurs = new Map();
  const io = new IntersectionObserver((entrees) => {
    for (const { target: g, intersectionRatio } of entrees) {
      if (intersectionRatio >= 0.6) {
        if (!minuteurs.has(g)) {
          minuteurs.set(g, setTimeout(() => {
            minuteurs.delete(g);
            io.unobserve(g);
            charger(g, 1);
          }, REGARD_MS));
        }
      } else {
        clearTimeout(minuteurs.get(g));
        minuteurs.delete(g);
      }
    }
  }, { threshold: [0, 0.6] });
  const observer = (racine) => racine.querySelectorAll('[data-sdm-galerie]').forEach((g) => io.observe(g));
  observer(document);
  // l'éditeur de thème redessine une section : ses nouvelles cartes
  document.addEventListener('shopify:section:load', (e) => observer(e.target));
}
